import React, { useState } from 'react';
import BlogManager from '../components/admin/BlogManager';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ArrowTopRightOnSquareIcon as ExternalLink, 
  DocumentTextIcon as FileText, 
  Cog6ToothIcon as Settings 
} from '@heroicons/react/24/outline';

const Admin: React.FC = () => {
  const [activeTab, setActiveTab] = useState('manager');

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="manager" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Blog Manager
            </TabsTrigger>
            <TabsTrigger value="tinacms" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              TinaCMS Editor
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manager">
            <BlogManager />
          </TabsContent>

          <TabsContent value="tinacms" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>TinaCMS Content Editor</CardTitle>
                <CardDescription>
                  Create and edit blog posts with the visual TinaCMS interface
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  TinaCMS provides a user-friendly interface for managing your blog content with:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li>Real-time validation and feedback</li>
                  <li>Rich text editing with markdown support</li>
                  <li>Image upload and management</li>
                  <li>Draft and publish workflow</li>
                  <li>Automatic slug generation</li>
                </ul>
                
                <div className="pt-4">
                  <a 
                    href="/admin/index.html" 
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="flex items-center gap-2">
                      Open TinaCMS Editor
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick Start Guide</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <h4 className="font-semibold mb-1">Creating a New Post:</h4>
                  <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                    <li>Click "Open TinaCMS Editor" above</li>
                    <li>Navigate to the Posts collection</li>
                    <li>Click "Create New" and fill in the required fields</li>
                    <li>Use the preview to see validation status</li>
                    <li>Save as draft or publish when ready</li>
                  </ol>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-1">Editing Existing Posts:</h4>
                  <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                    <li>Use the Blog Manager tab to see all posts</li>
                    <li>Click the edit icon next to any post</li>
                    <li>Make your changes in TinaCMS</li>
                    <li>Preview changes before saving</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;